import React, { FC, MutableRefObject, useCallback, useEffect, useRef, useState } from 'react';
import { CopyToClipboard as ReactCopyToClipboard } from 'react-copy-to-clipboard';
import styled from 'styled-components';
import * as tokens from '@utmost.co/utmost-design-tokens';
import { SystemIcon } from '../Icon/SystemIcon';
import { Tooltip, shouldEnableTooltip } from '../Tooltip/Tooltip';
import { Uds } from '../../types';
import { useIntlUds } from '../../translations/UdsIntlProvider';

export type CopyToClipboardProps = Uds<{
  valueToCopy: string;
  onCopy?: () => void;
  tooltipText?: string;
  /* Show tooltip only when ellipsis has appeared */
  tooltipOnlyWithEllipsis?: boolean;
  /* Component take 100% of parent element and base on it will add ellipsis if needed */
  enableEllipsis?: boolean;
  wrapText?: boolean;
}>;

const ICON_SIZE = 18;

export const CopyToClipboard: FC<React.PropsWithChildren<CopyToClipboardProps>> = ({
  children,
  valueToCopy,
  onCopy,
  tooltipText,
  tooltipOnlyWithEllipsis,
  enableEllipsis,
  dataTest,
  wrapText
}) => {
  const [showTooltip, setTooltipState] = useState(false);
  const [copied, setCopied] = useState(false);
  const typographyRef = useRef<HTMLElement>(null);

  const { intl } = useIntlUds();

  const onCopyHandler = useCallback(() => {
    setCopied(true);
    onCopy?.();
  }, [onCopy]);

  const onMouseEnter = useCallback(() => {
    if (tooltipOnlyWithEllipsis && typographyRef.current) {
      let ref = typographyRef.current;
      if (typographyRef.current.children.length > 0) {
        // If direct children is not a string, calculate reference base on children element
        // eslint-disable-next-line prefer-destructuring
        ref = typographyRef.current.children[0] as HTMLElement;
      }
      setTooltipState(shouldEnableTooltip(ref));
    }
  }, [tooltipOnlyWithEllipsis]);

  useEffect(() => {
    if (copied) {
      setTimeout(() => {
        setCopied(false);
      }, 1000);
    }
  }, [copied]);

  const shouldShowTooltip = (tooltipOnlyWithEllipsis ? showTooltip && tooltipText : tooltipText) || copied;

  return (
    <StyledReactCopyToClipboard data-test={dataTest}>
      <ReactCopyToClipboard text={valueToCopy} onCopy={onCopyHandler}>
        <Tooltip
          key={copied.toString()}
          title={
            copied
              ? intl.CopyToClipboard.tooltipText.afterCopy
              : (tooltipText ?? intl.CopyToClipboard.tooltipText.beforeCopy)
          }
          placement="top"
          disableHoverListener={!shouldShowTooltip}
        >
          <StyledContent $fullWidth={enableEllipsis} onMouseEnter={onMouseEnter}>
            <StyledText $wrapText={wrapText} ref={typographyRef as unknown as MutableRefObject<HTMLDivElement>}>
              <span data-test="copy-clipboard-content">{children}</span>
            </StyledText>
            {copied ? (
              <SystemIcon size="small" color={tokens.colorSuccess} className="copy-icon" name="check_circle" />
            ) : (
              <SystemIcon size="small" className="copy-icon" name="file_copy" />
            )}
          </StyledContent>
        </Tooltip>
      </ReactCopyToClipboard>
    </StyledReactCopyToClipboard>
  );
};

const StyledReactCopyToClipboard = styled.div`
  width: calc(100% - ${ICON_SIZE}px);
  > span {
    width: 100%;
  }
`;

export const StyledContent = styled.div<{ $fullWidth?: boolean }>`
  cursor: pointer;
  position: relative;
  width: ${({ $fullWidth }) => ($fullWidth ? '100%' : 'auto')};
  display: inline-block;
  .copy-icon {
    opacity: 0;
    position: absolute;
    top: 50%;
    right: -${ICON_SIZE}px;
    transform: translateY(-50%);
    transition: opacity 0.3s ease 0s;
  }
  &:hover {
    .copy-icon {
      opacity: 1;
    }
  }
`;

export const StyledText = styled.div<{ $wrapText?: boolean }>`
  vertical-align: middle;
  display: inline-block;
  overflow: hidden;
  white-space: ${({ $wrapText }) => ($wrapText ? 'wrap' : 'nowrap')};
  text-overflow: ellipsis;
  * {
    overflow: hidden;
    white-space: ${({ $wrapText }) => ($wrapText ? 'wrap' : 'nowrap')};
    text-overflow: ellipsis;
  }
  width: inherit;
  &:hover {
    text-decoration: underline ${({ theme }) => theme.colorPrimary};
  }
`;
